<template>
    <div
        id="cancelBookingModal"
        aria-hidden="true"
        aria-labelledby="cancelBookingModalLabel"
        class="modal fade modal-lg"
        tabindex="-1"
    >
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 id="cancelBookingModalLabel" class="modal-title">Cancel Booking {{ booking.uuid }}?</h5>
                    <button aria-label="Close" class="btn-close" data-bs-dismiss="modal" type="button"></button>
                </div>
                <div class="modal-body">
                    <SimpleBookingCard class="mb-3" :booking="booking"/>

                    <div class="mb-2">
                        <FormSelect
                            v-if="reason"
                            :key="reason.uuid"
                            v-model:selection="reason"
                            :options="cancellationReasons.filter(reason => reason.is_deleted === false)
                                .map(reason => {
                                    return {
                                            value: reason.uuid,
                                            label: reason.name
                                        }
                                })"
                            label="Cancellation Reason"
                            placeholder="Select Reason"
                        />
                    </div>

                    <div class="mb-2">
                        <FormTextarea
                            v-if="comment"
                            :key="comment.uuid"
                            v-model:input="comment"
                            label="Comment"
                            maxlength="250"
                            placeholder="(optional)"
                        />
                    </div>

                    <div>
                        <transition name="basic-fade">
                            <div class="alert alert-warning mt-2" v-if="!cancelAllInSequence">
                                <strong>Warning</strong>: This stop is part of a multi-stop booking. If it was part of a booking with multiple clients, cancelling this stop only will move related bookings for this client to a separate booking form.
                            </div>
                        </transition>

                        <transition name="basic-fade">
                            <div class="alert alert-info mt-2" v-if="cancellationFees.length > 0 && cancelAllInSchedule">
                                <strong>Please Note</strong>: By selecting to cancel all future rescheduled bookings, the entirety of the cancellation fee will be applied to first booking in the recurrence only.
                                If you wish to apply a cancellation fee to each booking, please cancel each booking separately.
                            </div>
                        </transition>

                        <transition name="basic-fade">
                            <div class="alert alert-info mt-2" v-if="percentageFees.length > 0">
                                <strong>Please note:</strong> This cancellation reason incurs a percentage-based fee. The fee will be calculated individually for each stop cancelled in the booking sequence.
                            </div>
                        </transition>

                        <transition name="basic-fade">
                            <div class="alert alert-info mt-2" v-if="fixedFees.length > 0">
                                <strong>Please note:</strong> This cancellation reason incurs a fixed fee and only applies if the first stop in the booking sequence is cancelled. The fee will be applied in full to the first stop.
                            </div>
                        </transition>

                        <div class="mt-2" v-if="booking.sequence_identifier !== null">
                            <FormSwitch id="cancelAllInSequenceCheckbox" label="Cancel all bookings on the same day linked with this one" v-model="cancelAllInSequence"></FormSwitch>
                        </div>

                        <div class="mt-2" v-if="booking.group_booking_identifier !== null">
                            <FormSwitch id="cancelAllInGroupCheckbox" label="Cancel all bookings in this group" v-model="cancelAllInGroup"></FormSwitch>
                        </div>

                        <div class="mt-2" v-if="booking.reschedule_identifier !== null">
                            <FormSwitch id="cancelAllRescheduledCheckbox" label="Cancel scheduled recurring bookings" v-model="cancelAllInSchedule"></FormSwitch>
                            <div class="mt-2" v-if="cancelAllInSchedule">
                                <div class="form-check">
                                    <label class="form-check-label">
                                        <input class="form-check-input" type="radio" v-model="cancelAllInScheduleDateRange" :value="false">
                                        Cancel all future bookings linked with this one
                                    </label>
                                </div>
                                <div class="form-check">
                                    <label class="form-check-label">
                                        <input class="form-check-input" type="radio" v-model="cancelAllInScheduleDateRange" :value="true"/>
                                        Cancel a date range of future bookings linked with this one
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div class="mt-2" >
                            <div v-if="cancelAllInScheduleDateRange" class="bg-light p-2">
                                <div class="row mb-2">
                                    <div class="col-6">
                                        <label for="date_from"> Date range from:</label>
                                        <input type="date" v-model="dateFrom" class="form-control"/>
                                    </div>
                                    <div class="col-6">
                                        <label for="date_from">Until:</label>
                                        <input type="date" v-model="dateTo" class="form-control"/>
                                    </div>
                                </div>
                            </div>
                            <div class="bg-light p-2 mt-2" v-if="!isEmpty(bookings)">
                                {{ bookings.length }} additional
                                {{ bookings.length === 1 ? 'booking' : 'bookings' }} will also be cancelled
                                <a @click="showBookings = !showBookings" href="javascript: void(0)">
                                    <template v-if="showBookings">
                                        Hide details
                                    </template>
                                    <template v-else>
                                        See details
                                    </template>
                                </a>

                                <transition name="basic-fade">
                                    <div class="card shadow-none mt-2" v-if="showBookings">
                                        <div class="card-body p-0">
                                            <div class="row mx-0">
                                                <template v-for="booking in bookings">
                                                    <div class="col-3 d-flex flex-row align-items-center p-2 border-top-0 border-start-0 border border-light">
                                                        <BookingListLinkIcon :booking="booking" class="me-1"/>
                                                        <DateTime :date-time="booking.date" format="medium date"></DateTime>
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </transition>
                            </div>
                        </div>
                    </div>

                    <template v-if="cancellationFees.length > 0">
                        <div class="form-check mt-2">
                            <input id="waiveBookingCancellationFeeCheckbox" v-model="waiveFee" class="form-check-input"
                                   type="checkbox" value="">
                            <label class="form-check-label" for="waiveBookingCancellationFeeCheckbox">
                                Waive cancellation fee
                            </label>
                        </div>
                        <div class="py-2 px-4 mt-2 rounded border-1 bg-light">
                            <div class="my-2">
                                <strong>Cancellation Fees</strong>
                            </div>
                            <template v-if="!calculatingFees">
                                <template v-for="fee in cancellationFees">
                                    <div class="mb-2">
                                        <div class="d-flex justify-content-between">
                                            <div>{{ fee.description }}</div>
                                            <div>
                                                {{ $filters.currency(fee.amount) }}
                                                <template v-if="fee.gst > 0">
                                                    (+ {{ $filters.currency(fee.gst) }} GST)
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <div v-if="waiveFee" class="d-flex justify-content-between mb-2 border-top pt-2">
                                    <div><strong>Fee Waived</strong></div>
                                    <div>-{{ $filters.currency(totalFee) }}</div>
                                </div>
                                <div class="d-flex justify-content-between border-top pt-2">
                                    <div><strong>Total</strong></div>
                                    <div><strong>{{ $filters.currency(waiveFee ? 0 : totalFee) }}</strong></div>
                                </div>
                            </template>
                            <div class="d-flex align-items-center justify-content-center py-2">
                                <Spinner v-if="calculatingFees"/>
                            </div>
                        </div>
                    </template>

                </div>
                <div class="modal-footer">
                    <button class="btn btn-secondary" data-bs-dismiss="modal" type="button">Close</button>
                    <Button :loading="cancelling" :disabled="!reason || reason.value === null" color="danger" @click="cancelBooking">Confirm Cancellation</Button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BookingCancellationReason from "@classes/BookingCancellationReason";
import BookingListLinkIcon from "@components/Bookings/BookingListLinkIcon.vue";
import { CancelBookingInterface } from "@components/Bookings/CancelBooking/index";
import SimpleBookingCard from "@components/Bookings/SimpleBookingCard.vue";
import Button from "@components/Button.vue"
import DateTime from "@components/DateTime.vue";
import FormSelect from "@components/Forms/FormSelect.vue";
import FormSwitch from "@components/Forms/FormSwitch.vue";
import FormTextarea from "@components/Forms/FormTextarea.vue";
import Spinner from "@components/Spinner.vue"
import { ToastInterface } from "@components/toast";
import { LaravelResourceResponse } from "@customTypes/LaravelCommonTypes";
import { watchDebounced } from "@vueuse/core";
import { Axios, AxiosResponse } from "axios";
import { Modal } from 'bootstrap';
import { isEmpty } from "lodash";
import { DateTime as LuxonDateTime } from "luxon";
import { computed, inject, onMounted, ref, watch } from "vue";
import { uuid } from "vue-uuid";
import { Ref } from "vue/dist/vue";
import BookingResource = App.Resource.BookingResource;

interface FeeInterface {
    amount: number,
    gst: number,
    unit: string,
    description: string
}

const axios: Axios = <Axios>inject("axios");
const toast: ToastInterface = <ToastInterface>inject('toast')

const cancellationModal: Ref<Modal | undefined> = ref<Modal>()
const booking: Ref<BookingResource> = <BookingResource>inject('bookingToBeCancelled', {})
const cancellationReasons: Ref<BookingCancellationReason[]> = ref([])

const reason: Ref<{ uuid: string, value: string | null, errors: [], required: boolean } | null> = ref(null);
const comment: Ref<{ uuid: string, value: string, errors: [], required: boolean } | null> = ref(null);
const waiveFee: Ref<boolean> = ref(false);

const bookings = ref([])
const loading = ref(false)
const showBookings = ref(false)

const cancelAllInSequence: Ref<boolean> = ref(false)
const cancelAllInSchedule: Ref<boolean> = ref(false)
const cancelAllInGroup: Ref<boolean> = ref(false)
const cancelAllInScheduleDateRange: Ref<boolean> = ref<boolean>(false)
const dateFrom: Ref<LuxonDateTime | undefined> = ref<LuxonDateTime>();
const dateTo: Ref<LuxonDateTime | undefined> = ref<LuxonDateTime>();

const getBookings = function () {
    if (isEmpty(booking.value)) {
        return;
    }
    loading.value = true
    axios
        .get(route('api.bookings.cancel', { booking: booking.value.uuid }), {
            params: {
                cancel_all_in_sequence: cancelAllInSequence.value ? 1 : 0,
                cancel_all_in_schedule: cancelAllInSchedule.value ? 1 : 0,
                cancel_all_in_group: cancelAllInGroup.value ? 1 : 0,
                date_from: cancelAllInScheduleDateRange.value ? dateFrom.value : null,
                date_to: cancelAllInScheduleDateRange.value ? dateTo.value : null,
                list: 1,
            }
        })
        .then(response => {
            bookings.value = response.data.data;
        })
        .catch(error => {
            toast.error('An error occurred, please try again later.')
            console.error(error.message);
        })
        .finally(() => {
            loading.value = false
        })
}

const setDefaultFormValues = () => {
    reason.value = {
        uuid: uuid.v4(),
        value: null,
        errors: [],
        required: true,
    }
    comment.value = {
        uuid: uuid.v4(),
        value: '',
        errors: [],
        required: false,
    }
    waiveFee.value = false
}

watch(booking, (newValue) => {
    setDefaultFormValues()
    if (isEmpty(newValue)) {
        return
    }

    let bookingInfoModal = new Modal(document.getElementById('bookingDetailsModal') as Element);
    bookingInfoModal.hide()
    cancellationModal.value?.show()
})

const calculatingFees = ref(false)
const cancellationFees: Ref<FeeInterface[]> = ref([])

const totalFee = computed(() => cancellationFees.value.reduce((total, fee) => total + fee.amount + fee.gst, 0))
const percentageFees = computed(() => cancellationFees.value.filter((fee: FeeInterface) => fee.unit === '%'));
const fixedFees = computed(() => cancellationFees.value.filter((fee: FeeInterface) => fee.unit !== '%'));

const calculateCancellationFees = () => {

    if (!reason.value || !reason.value.value) {
        cancellationFees.value = []
        return
    }

    calculatingFees.value = true
    axios
        .get(route('api.bookings.calculate-cancellation-fees', { booking: booking.value.uuid, reason: reason.value.value }))
        .then(response => {
            cancellationFees.value = response.data.data
        })
        .catch(error => {
            toast.error('Failed to get cancellation fees')
            console.error(error.message);
        })
        .finally(() => {
            calculatingFees.value = false
        })
}

const cancelling = ref(false)
const cancelBookingPlugin: CancelBookingInterface = <CancelBookingInterface>inject('cancelBooking')

const cancelBooking = function () {
    cancelling.value = true;
    axios.post(route('api.bookings.cancel', { booking: booking.value.uuid }), {
        reason: reason.value?.value,
        comment: comment.value?.value,
        waive_fee: waiveFee.value,
        cancel_all_in_sequence: cancelAllInSequence.value,
        cancel_all_in_group: cancelAllInGroup.value,
        cancel_all_in_schedule: cancelAllInSchedule.value,
        date_from: dateFrom.value,
        date_to: dateTo.value,
    }).then(async () => {
        booking.value.updated_at = Date.now()
        cancelBookingPlugin.updateCancelledBooking(booking.value)
        await toast.success(`Your cancellation request of ${ booking.value.uuid } is accepted and being processed.`, { timeout: 3000 })
        cancellationModal.value?.hide()
        if (cancelBookingPlugin.reloadOnSubmit()) {
            window.location.reload()
        }
    }).catch(error => {
        toast.error('An error occurred, please try again later.')
        console.error(error.message);
    }).finally(() => {
        cancelling.value = false
    })
}

const getCancellationReasons = async () => {
    let response: AxiosResponse<LaravelResourceResponse<BookingCancellationReason[]>> = await BookingCancellationReason.index();
    cancellationReasons.value = response.data.data;
}

watch(reason, () => calculateCancellationFees())
watchDebounced(dateFrom, () => getBookings(), { debounce: 500 });
watchDebounced(dateTo, () => getBookings(), { debounce: 500 });
watchDebounced(cancelAllInSequence, () => getBookings(), { debounce: 500 });
watchDebounced(cancelAllInSchedule, () => getBookings(), { debounce: 500 });
watchDebounced(cancelAllInGroup, () => getBookings(), { debounce: 500 });
watchDebounced(cancelAllInScheduleDateRange, () => getBookings(), { debounce: 500 });

watch(booking, (newValue) => {
    setDefaultFormValues()
    if (isEmpty(newValue)) {
        return
    }

    let bookingInfoModal = Modal.getInstance(document.getElementById('bookingDetailsModal') as Element);
    bookingInfoModal?.hide()
    cancellationModal.value?.show()
})

onMounted(() => {
    cancellationModal.value = new Modal(document.getElementById('cancelBookingModal') as Element);
    document.getElementById('cancelBookingModal')?.addEventListener('show.bs.modal', () => getCancellationReasons());
    document.getElementById('cancelBookingModal')?.addEventListener('hide.bs.modal', () => {
        booking.value = {};
        bookings.value = [];
        cancelAllInSchedule.value = false;
        cancelAllInGroup.value = false;
        cancelAllInSequence.value = false;
        cancelAllInScheduleDateRange.value = false;
        showBookings.value = false;
        loading.value = false;
    })
})
</script>

<style scoped>

</style>
